import { z } from 'zod';
/**
 * Hotfix 34 — "CCTV Footage & Live Access" request. Implements every field of
 * the two-page paper form (sections A–E) plus its guidelines, with an approver
 * chosen by the requester from Microsoft Entra and an authenticated approval:
 *
 *   requester fills A–C + picks approver -> "Submit & Send for Approval"
 *   -> PENDING approval, email to the approver (Graph) with a sign-in link
 *   -> the APPROVER (that signed-in person, nobody else) approves / rejects
 *   -> IT/Security staff record receipt and fulfilment (only after approval).
 *
 * Choosing an approver is never an approval. Approval and fulfilment fields
 * are never accepted from the requester's create payload (strict schema).
 * The "signature" fields are authenticated acknowledgements (typed name +
 * signed-in account + server timestamp), NOT certified digital signatures.
 * Nothing here grants CCTV system access, issues download links or talks to
 * a camera system; footage is never attached or emailed.
 */
export const CCTV_CATEGORY_CODE = 'CCTV_ACCESS';
export const CCTV_REQUEST_TYPES = ['FOOTAGE', 'LIVE_ACCESS'];
export const CCTV_REQUEST_TYPE_LABELS = {
    FOOTAGE: 'Recorded footage review',
    LIVE_ACCESS: 'Live view access',
};
export const CCTV_APPROVAL_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'];
export const CCTV_APPROVAL_STATUS_LABELS = {
    PENDING: 'Pending Approval',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
};
export const CCTV_EMAIL_STATUSES = ['PENDING', 'SENT', 'FAILED'];
export const CCTV_ACCESS_MODES = ['VIEW', 'DOWNLOAD', 'VIEW_AND_DOWNLOAD'];
export const CCTV_ACCESS_MODE_LABELS = {
    VIEW: 'View',
    DOWNLOAD: 'Download',
    VIEW_AND_DOWNLOAD: 'View and download',
};
export const CCTV_ACCESS_SLA_STATES = ['ON_TRACK', 'AT_RISK', 'BREACHED', 'MET'];
/** The access turnaround target from the form's guidelines: four WORKING hours, measured from submission. */
export const CCTV_ACCESS_TARGET_MINUTES = 240;
/** Warn when this share of the target has been used (matches the SLA policies' 80 % default). */
export const CCTV_ACCESS_WARNING_PERCENT = 80;
export const CCTV_DECLARATION_TEXT = 'I confirm that this request is for official purposes only. The footage will be handled in compliance with organizational policy and privacy regulations.';
/** The form's guidelines, same meaning. Shown before submission, on the request, and in the approval email. */
export const CCTV_GUIDELINES = [
    'CCTV recording requests must be placed through the hospital security in-charge.',
    'The security in-charge must escort the requester to review recordings, with the proper approvals in place.',
    'Access is individual: groups must not enter the CCTV command area.',
    'The access turnaround target is four working hours.',
    'Recording access is for valid incidents and official purposes only, never for personal use.',
];
export const CCTV_SIGNATURE_NOTE = 'Signed by typing your name while signed in to ResolveIT. Your account and the server time are recorded. This is an acknowledgement, not a certified digital signature.';
export const CCTV_FIELD_LABELS = {
    requesterFullName: 'Full Name',
    employeeId: 'Employee ID',
    department: 'Department',
    designation: 'Designation',
    contact: 'Contact No. / Email',
    requestDate: 'Date of Request',
    purpose: 'Purpose of Request',
    incidentDescription: 'Incident Description',
    cameraLocation: 'Location of Camera/Area',
    footageStartDate: 'Footage start date',
    footageEndDate: 'Footage end date',
    footageTimeFrom: 'Time frame — from',
    footageTimeTo: 'Time frame — to',
    footageOvernight: 'Time frame runs past midnight',
    declarationSignedName: 'Signature of Requestor (type your full name)',
    approver: 'Approver',
    approverName: 'Name of Approver',
    approverDesignation: 'Designation',
    approverDepartment: 'Department',
    approvalRemarks: 'Remarks',
    approvalStatus: 'Approval Status',
    approvalSignedName: 'Signature of Approver (type your full name)',
    receivedBy: 'Request Received By',
    accessProvided: 'Access Provided',
    footageRetrievedAt: 'Date & Time Footage Retrieved',
    accessMode: 'Mode of Access',
    handledBy: 'Handled By',
    handledSignedName: 'Signature (type your full name)',
};
export const CCTV_MAX = {
    requesterFullName: 200,
    employeeId: 50,
    department: 200,
    designation: 200,
    contact: 254,
    purpose: 2000,
    incidentDescription: 4000,
    cameraLocation: 500,
    signedName: 200,
    remarks: 2000,
};
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
function isRealDate(s) {
    if (!DATE_RE.test(s))
        return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
const reqText = (max) => z.string().trim().min(1, 'Required.').max(max);
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));
const optDate = z
    .string()
    .trim()
    .nullish()
    .transform((v) => (v ? v : null));
/**
 * `POST /tickets` `cctvRequest` (category CCTV_ACCESS only). Requester-owned
 * sections only — `.strict()` refuses any approval or IT/Security field.
 * `approverRef` is a people-picker ref (`user:<id>` or `entra:<objectId>`)
 * the server re-reads from the directory; free text is never an approver.
 * The request type comes from the ticket's Type (subcategory FOOTAGE / LIVE_ACCESS);
 * "Date of Request" is the server's business-timezone date of submission.
 */
export const cctvRequestCreateSchema = z
    .object({
    requesterFullName: reqText(CCTV_MAX.requesterFullName),
    employeeId: reqText(CCTV_MAX.employeeId),
    department: reqText(CCTV_MAX.department),
    designation: reqText(CCTV_MAX.designation),
    contact: reqText(CCTV_MAX.contact),
    purpose: reqText(CCTV_MAX.purpose),
    incidentDescription: optText(CCTV_MAX.incidentDescription),
    cameraLocation: reqText(CCTV_MAX.cameraLocation),
    footageStartDate: optDate,
    footageEndDate: optDate,
    footageTimeFrom: optDate,
    footageTimeTo: optDate,
    footageOvernight: z.boolean().optional().default(false),
    declarationAccepted: z.literal(true, { errorMap: () => ({ message: 'Accept the declaration to submit.' }) }),
    declarationSignedName: reqText(CCTV_MAX.signedName),
    approverRef: z.string().trim().min(1, 'Select an approver from the directory.').max(200),
})
    .strict();
/**
 * Footage-period rules, shared by the form and the API (the API is
 * authoritative). `today` is the business-timezone date (YYYY-MM-DD). The
 * requested footage period is wall-clock time in the business timezone; it
 * is distinct from the later "Date & Time Footage Retrieved".
 *
 * - FOOTAGE: start/end dates and from/to times are all required.
 * - LIVE_ACCESS: footage fields are optional, but if any is given the set must be complete and valid.
 * - end date >= start date; footage cannot start in the future.
 * - An end time at or before the start time on the same day is refused unless
 *   "runs past midnight" is ticked (never silently reinterpreted). With it
 *   ticked the period runs from the start date `from` to the following day's `to`,
 *   so the end date must then be after the start date or equal to it (one night).
 */
export function validateCctvFootage(d, requestType, today) {
    const e = {};
    const any = !!(d.footageStartDate || d.footageEndDate || d.footageTimeFrom || d.footageTimeTo);
    if (requestType === 'LIVE_ACCESS' && !any)
        return e;
    if (!d.footageStartDate)
        e.footageStartDate = 'Enter the start date of the footage needed.';
    else if (!isRealDate(d.footageStartDate))
        e.footageStartDate = 'Enter a valid date.';
    if (!d.footageEndDate)
        e.footageEndDate = 'Enter the end date of the footage needed.';
    else if (!isRealDate(d.footageEndDate))
        e.footageEndDate = 'Enter a valid date.';
    if (!d.footageTimeFrom)
        e.footageTimeFrom = 'Enter the start time.';
    else if (!TIME_RE.test(d.footageTimeFrom))
        e.footageTimeFrom = 'Enter a time as HH:MM (24-hour).';
    if (!d.footageTimeTo)
        e.footageTimeTo = 'Enter the end time.';
    else if (!TIME_RE.test(d.footageTimeTo))
        e.footageTimeTo = 'Enter a time as HH:MM (24-hour).';
    if (Object.keys(e).length)
        return e;
    const s = d.footageStartDate;
    const en = d.footageEndDate;
    if (s > today)
        e.footageStartDate = 'Footage cannot start in the future.';
    if (en < s)
        e.footageEndDate = 'The end date cannot be before the start date.';
    else if (s === en && d.footageTimeTo <= d.footageTimeFrom && !d.footageOvernight) {
        e.footageTimeTo = 'The end time is not after the start time. Tick "runs past midnight" if the footage spans overnight.';
    }
    return e;
}
/** `POST /tickets/:id/cctv/decision` — the assigned approver only (server-enforced). */
export const cctvDecisionSchema = z
    .object({
    decision: z.enum(['APPROVED', 'REJECTED']),
    remarks: optText(CCTV_MAX.remarks),
    signedName: reqText(CCTV_MAX.signedName),
})
    .strict();
/**
 * `POST /tickets/:id/cctv/fulfilment` — IT staff (Admin / Team Lead / Technician)
 * who can work the ticket. `RECEIVE` records "Request Received By" (allowed
 * while approval is pending). `COMPLETE` records Access Provided / retrieval time /
 * mode / Handled By and is refused unless the request is APPROVED.
 */
export const cctvFulfilmentSchema = z
    .discriminatedUnion('action', [
    z.object({ action: z.literal('RECEIVE') }).strict(),
    z
        .object({
        action: z.literal('COMPLETE'),
        accessProvided: z.boolean(),
        footageRetrievedAt: z.coerce.date().nullish().transform((v) => v ?? null),
        accessMode: z.enum(CCTV_ACCESS_MODES).nullish().transform((v) => v ?? null),
        signedName: reqText(CCTV_MAX.signedName),
    })
        .strict(),
])
    .superRefine((d, ctx) => {
    if (d.action !== 'COMPLETE')
        return;
    if (d.accessProvided && !d.accessMode) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['accessMode'], message: 'Select the mode of access provided.' });
    }
    if (!d.accessProvided && d.accessMode) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['accessMode'], message: 'No mode of access when access was not provided.' });
    }
});
const userNameRef = z.object({ id: z.string().uuid(), displayName: z.string() }).nullable();
/** On ticket detail (`cctvRequest`), for viewers who can see the ticket. */
export const cctvRequestRecordSchema = z.object({
    requestType: z.enum(CCTV_REQUEST_TYPES),
    requesterFullName: z.string(),
    employeeId: z.string(),
    department: z.string(),
    designation: z.string(),
    contact: z.string(),
    requestDate: z.string(), // YYYY-MM-DD
    purpose: z.string(),
    incidentDescription: z.string().nullable(),
    cameraLocation: z.string(),
    footageStartDate: z.string().nullable(),
    footageEndDate: z.string().nullable(),
    footageTimeFrom: z.string().nullable(), // HH:MM
    footageTimeTo: z.string().nullable(),
    footageOvernight: z.boolean(),
    declaration: z.object({
        accepted: z.boolean(),
        signedName: z.string(),
        signedBy: userNameRef,
        signedAt: z.date(),
    }),
    approver: z.object({
        userId: z.string().uuid(),
        name: z.string(),
        email: z.string(),
        designation: z.string().nullable(),
        department: z.string().nullable(),
    }),
    approval: z.object({
        status: z.enum(CCTV_APPROVAL_STATUSES),
        remarks: z.string().nullable(),
        signedName: z.string().nullable(),
        decidedBy: userNameRef,
        decidedAt: z.date().nullable(),
    }),
    approvalEmail: z.object({
        status: z.enum(CCTV_EMAIL_STATUSES),
        sentAt: z.date().nullable(),
        attempts: z.number().int().nonnegative(),
    }),
    fulfilment: z.object({
        receivedBy: userNameRef,
        receivedAt: z.date().nullable(),
        accessProvided: z.boolean().nullable(),
        footageRetrievedAt: z.date().nullable(),
        accessMode: z.enum(CCTV_ACCESS_MODES).nullable(),
        handledBy: userNameRef,
        handledSignedName: z.string().nullable(),
        handledAt: z.date().nullable(),
    }),
    accessTarget: z.object({
        targetMinutes: z.number().int().positive(),
        dueAt: z.date().nullable(),
        state: z.enum(CCTV_ACCESS_SLA_STATES),
    }),
    /** What THIS viewer may do — computed server-side by the same policy the API enforces. */
    can: z.object({
        decide: z.boolean(),
        receive: z.boolean(),
        complete: z.boolean(),
        retryEmail: z.boolean(),
    }),
    /**
     * True when the viewer can see this ticket ONLY because they are its selected approver: a read-only view of
     * the request plus the decision. The page then hides comments, attachments and the activity feed (the API
     * answers those with 404 for this viewer). Absent otherwise.
     */
    approverOnlyView: z.boolean().optional(),
});
//# sourceMappingURL=cctv-request.js.map